import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";

import { Stack } from "@fluentui/react";
import { CoObjectItem, CoObjectSummary, CoObjectType } from "../../interfaces/CoObject";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { Checkbox } from "@progress/kendo-react-inputs";
import { ObjectListItemButton, ObjectListItemRow } from "./ObjectListItemRow";
import { FilterBar, FilterItem } from "../../generic/filterBar/FilterBar";
import { CoObjectTypeEnum } from "../../../api/models";
import { ObjectListItemRowExpandable } from "./ObjectListItemRowExpandable";

const getObjectIcon = (type: CoObjectType | CoObjectTypeEnum) => {
  // eslint-disable-next-line react/display-name
  return () => <span className={`${type} icon_class`} />;
};

const defaultObjectFilters: FilterItem[] = [
  {
    key: "all",
    label: formatMessage("cof.objectlist.filters.allobjects"),
    value: undefined,
  },
  {
    key: "amount",
    label: formatMessage("cof.createnewobject.amount"),
    value: [CoObjectTypeEnum.AMOUNT, CoObjectTypeEnum.AMOUNT_AIH],
    icon: getObjectIcon(CoObjectTypeEnum.AMOUNT),
  },
  {
    key: "checkbox",
    label: formatMessage("cof.createnewobject.checkbox"),
    value: CoObjectTypeEnum.CHECKBOX,
    icon: getObjectIcon(CoObjectTypeEnum.CHECKBOX),
  },
  {
    key: "dropdown",
    label: formatMessage("cof.createnewobject.dropdown"),
    value: CoObjectTypeEnum.DROPDOWN_LIST,
    icon: getObjectIcon(CoObjectTypeEnum.DROPDOWN_LIST),
  },
  {
    key: "dimensionattribute",
    label: formatMessage("cof.objectsource.dimension"),
    value: CoObjectTypeEnum.DIMENSION_ATTRIBUTE,
    icon: getObjectIcon(CoObjectTypeEnum.DIMENSION_ATTRIBUTE),
  },
  {
    key: "table",
    label: formatMessage("cof.createnewobject.table"),
    value: CoObjectTypeEnum.TABLE,
    icon: getObjectIcon(CoObjectTypeEnum.TABLE),
  },
  {
    key: "freetext",
    label: formatMessage("cof.createnewobject.text"),
    value: [CoObjectTypeEnum.FREE_TEXT, CoObjectTypeEnum.TGK_TEXT],
    icon: getObjectIcon(CoObjectTypeEnum.FREE_TEXT),
  },
  {
    key: "chart",
    label: formatMessage("cof.createnewobject.chart"),
    value: CoObjectTypeEnum.CHART,
    icon: getObjectIcon(CoObjectTypeEnum.CHART),
  },
];

export interface ObjectListItem {
  object: CoObjectItem;
  disableSelection?: boolean;
  selected: boolean;
  highlighted: boolean;
  buttons?: ObjectListItemButton[];
}

export const ObjectList = ({
  objects,
  disableSelectAll = false,
  multiselectable = false,
  expandableObjects = false,
  allowFiltering = false,
  showObjectIcons = true,
  showStatusIcons = true,
  showComponentIcons = true,
  onItemClick,
  onSelectionChange,
  style,
  objectFilters,
  preselectedCellsMap,
}: {
  objects: ObjectListItem[];
  disableSelectAll?: boolean;
  multiselectable?: boolean;
  expandableObjects?: boolean;
  allowFiltering?: boolean;
  showObjectIcons?: boolean;
  showStatusIcons?: boolean;
  showComponentIcons?: boolean;
  onItemClick?: (item: CoObjectItem, shiftKey?: boolean) => void;
  onSelectionChange?: (selectedItems: CoObjectItem[]) => void;
  style?: CSSProperties;
  objectFilters?: FilterItem[];
  preselectedCellsMap?: Map<string, CoObjectSummary[]>;
}) => {
  const [lastSelectedWithoutShift, setLastSelectedWithoutShift] = useState<CoObjectItem | null>(null);
  const [numberOfSelectedObjects, setNumberOfSelectedObjects] = useState(0);
  const [currentQuery, setCurrentQuery] = useState("");
  const [currentFilters, setCurrentFilters] = useState<CoObjectTypeEnum[]>([]);
  const selectedTableCellsRef = useRef<Map<string, CoObjectItem[]>>(new Map());
  const [listeningObjectId, setListeningObjectId] = useState<string | null>(null);

  const inViewObjects = useMemo(
    () =>
      allowFiltering
        ? objects.filter(
            (obj) =>
              (currentFilters.length === 0 || currentFilters.find((f) => f === obj.object.info.objectType)) &&
              (currentQuery === "" || obj.object.info.objectName.toLowerCase().indexOf(currentQuery) > -1)
          )
        : objects,
    [currentFilters, currentQuery, objects, allowFiltering]
  );
  const selectableObjects = useMemo(() => inViewObjects.filter((obj) => !obj.disableSelection), [inViewObjects]);

  useEffect(() => {
    setNumberOfSelectedObjects(selectableObjects.filter((obj) => obj.selected).length);
    if (selectableObjects.find((obj) => obj.selected && lastSelectedWithoutShift?.id === obj.object.id) === undefined) {
      setLastSelectedWithoutShift(null);
    }
  }, [selectableObjects]);

  const handleToggleSelect = (item: CoObjectItem, selected?: boolean, shiftKey?: boolean) => {
    if (!selectableObjects.find((obj) => obj.object.id === item.id)) {
      return; // Item is not selectable
    }

    let newSelectedObjects = new Map<string, CoObjectItem>();
    const isSelected = objects.find((obj) => obj.object.id === item.id)?.selected || false;

    if (shiftKey && lastSelectedWithoutShift) {
      // Select range
      const startIndex = selectableObjects.findIndex((obj) => obj.object.id === lastSelectedWithoutShift.id);
      const endIndex = selectableObjects.findIndex((obj) => obj.object.id === item.id);
      const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];

      for (let i = from; i <= to; i++) {
        newSelectedObjects.set(selectableObjects[i].object.id, selectableObjects[i].object);
      }
    } else {
      // Toggle single item
      selected ??= !isSelected;
      if (selected) {
        newSelectedObjects.set(item.id, item);
        setLastSelectedWithoutShift(item);
      } else if (lastSelectedWithoutShift?.id === item.id) {
        setLastSelectedWithoutShift(null);
      }
    }

    // Add previously selected items
    objects.forEach((obj) => {
      if (obj.selected && !newSelectedObjects.has(obj.object.id) && !(!selected && obj.object.id === item.id)) {
        newSelectedObjects.set(obj.object.id, obj.object);
      }
    });

    onSelectionChange?.(
      Array.from(newSelectedObjects.values()).concat(Array.from(selectedTableCellsRef.current.values()).flat())
    );
  };

  const handleTableCellsChange = (tableItem: CoObjectItem, cells: CoObjectItem[]) => {
    if (tableItem.id != null) selectedTableCellsRef.current.set(tableItem.id, cells);
    const regularSelected = objects.filter((o) => o.selected).map((o) => o.object);
    const allCells = Array.from(selectedTableCellsRef.current.values()).flat();
    onSelectionChange?.([...regularSelected, ...allCells]);
  };

  const handleToggleSelectAll = () => {
    const allCells = Array.from(selectedTableCellsRef.current.values()).flat();
    onSelectionChange?.([
      ...(numberOfSelectedObjects < selectableObjects.length
        ? selectableObjects.map((obj) => obj.object)
        : objects
            .filter((obj) => obj.selected && !selectableObjects.find((o) => obj.object.id === o.object.id))
            .map((obj) => obj.object)),
      ...allCells,
    ]);
  };

  return (
    <div style={style}>
      {allowFiltering && (
        <FilterBar
          onFilter={(query, filters) => {
            setCurrentQuery(query);
            setCurrentFilters(filters);
          }}
          filters={objectFilters ?? defaultObjectFilters}
          currentFilters={currentFilters}
          currentQuery={currentQuery}
          itemsFound={inViewObjects.length}
        />
      )}
      {multiselectable && !disableSelectAll && (
        <div style={{ marginBottom: "5px" }}>
          <Stack horizontal>
            <Checkbox
              id={"selectall"}
              value={numberOfSelectedObjects != 0 && numberOfSelectedObjects < selectableObjects.length ? null : true}
              checked={numberOfSelectedObjects == selectableObjects.length}
              size="large"
              rounded="small"
              style={{
                marginRight: "5px",
              }}
              onChange={handleToggleSelectAll}
            />
            <div style={{ fontWeight: "bold", color: "var(--color-blue)" }}>
              {formatMessage("cof.objectlist.selectall")}
            </div>
          </Stack>
        </div>
      )}
      {inViewObjects.map((objectListItem) => {
        const isListeningActive = listeningObjectId !== null;
        const isThisRowListening = objectListItem.object.id === listeningObjectId;
        const selectedCells =
          objectListItem.object.id != null
            ? selectedTableCellsRef.current.get(objectListItem.object.id) ??
              preselectedCellsMap?.get(objectListItem.object.id) ??
              []
            : [];
        return objectListItem.object.info.objectType == CoObjectTypeEnum.TABLE && expandableObjects ? (
          <ObjectListItemRowExpandable
            key={objectListItem.object.id}
            objectItem={objectListItem.object}
            onClick={onItemClick || undefined}
            onSelectedCellsChange={handleTableCellsChange}
            onListeningChange={(listening) => setListeningObjectId(listening ? objectListItem.object.id : null)}
            disabled={isListeningActive && !isThisRowListening}
            showObjectIcon={showObjectIcons}
            showStatusIcons={showStatusIcons}
            showComponentIcons={showComponentIcons}
            highlighted={objectListItem.highlighted}
            buttons={objectListItem.buttons}
            initialSelectedCells={selectedCells}
          />
        ) : (
          <div
            key={objectListItem.object.id}
            style={{ opacity: isListeningActive ? 0.4 : 1, pointerEvents: isListeningActive ? "none" : undefined }}
          >
            <ObjectListItemRow
              objectItem={objectListItem.object}
              onClick={onItemClick || undefined}
              onToggleSelect={multiselectable ? handleToggleSelect : undefined}
              showObjectIcon={showObjectIcons}
              showStatusIcons={showStatusIcons}
              showComponentIcons={showComponentIcons}
              selected={objectListItem.selected}
              selectable={!objectListItem.disableSelection}
              highlighted={objectListItem.highlighted}
              buttons={objectListItem.buttons}
            />
          </div>
        );
      })}
    </div>
  );
};
